// @ts-strict-ignore
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ArticleCollectionVO, ArticleVO } from '@article/model/vo/article.vo';
import { CustomValidators } from '@shared/custom-validators';
import { ArticleNumberMappingVO } from '@article/model/vo/article-number-mapping.vo';
import { ArticleNumberMappingUtils } from '@article/util/article-number-mapping-utils';
import { UserModel } from '@user/model/user.model';
import { ArticleCollectionUtils } from '@article/util/article-collection-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { StringUtils } from '@shared/util/string-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleManager } from '@article/manager/article.manager';
import { CollectionNotification, CollectionService } from '../../service/collection.service';
import { UserModelService } from '@user/model/user.model.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ArticleComponentsPresenter } from '@admintool/components/presenter/article-components.presenter';
import { Subscription } from 'rxjs';

/**
 * Add or edit the favorites library
 */
@Component({
  standalone: false,
  selector: 'edit-article-collection',
  templateUrl: './edit-article-collection.component.html'
})
export class EditArticleCollectionComponent implements OnInit, OnDestroy {

  public readonly ARTICLE_COLLECTION_NORMAL_TYPE: string = ArticleCollectionVO.TYPE_NORMAL;

  editForm: FormGroup;
  public title: string;
  public showRequiredFields = false;

  @Input() public articleCollection: ArticleCollectionVO = null;
  @Input() public collectionArticles: ArticleVO[] = [];
  @Output() public finish = new EventEmitter<ArticleCollectionVO | null>();

  private removedArticles: ArticleVO[] = [];

  public articlesSorting: string[] = [ArticleCollectionVO.SORT_BY_ARTICLE_NO, ArticleCollectionVO.SORT_BY_OWN_ARTICLE_NO];
  public currentSorting: string;

  public update: EventEmitter<void> = new EventEmitter<void>();

  private collectionNotificationSubscription: Subscription;

  constructor(
    private formBuilder: FormBuilder,
    private bsModalRef: BsModalRef,
    private userModel: UserModel,
    private articleManager: ArticleManager,
    private localeResourceManager: LocaleResourceManager,
    private collectionService: CollectionService,
    userModelService: UserModelService,
    private articleComponentsPresenter: ArticleComponentsPresenter
    ) {
    userModelService.articleNumberMappingsChanged$.pipe(takeUntilDestroyed()).subscribe(() => this.articleNumberMappingsUpdatedHandler());
  }

  public ngOnInit(): void {
    this.title = !this.articleCollection ? 'editArticlePopup.header.new' : 'editArticlePopup.header.edit';
    const collectionName: string = !this.articleCollection ? '' : this.getArticleCollectionLabel(this.articleCollection.name);
    this.currentSorting = this.articleCollection ? this.articleCollection.sorting : ArticleCollectionVO.SORT_BY_ARTICLE_NO;
    this.sortArticles();
    this.editForm = this.formBuilder.group({
      articleCollectionName: [collectionName, CustomValidators.trimRequired]
    });
    this.collectionNotificationSubscription = this.collectionService.collectionNotification$.subscribe((data:CollectionNotification) => this.onCollectionNotification(data));
  }

  public ngOnDestroy(): void {
    this.collectionNotificationSubscription.unsubscribe();
  }

  private onCollectionNotification(data: CollectionNotification): void {
    if (data.notification === this.collectionService.NOTIFICATION_UPDATE_FAVORITE_ARTICLES
        || data.notification === this.collectionService.NOTIFICATION_SINGLE_COLLECTION_CHANGED) {
      this.articleNumberMappingsUpdatedHandler();
    }
  }

  public close(): void {
    this.bsModalRef.hide();
    this.finish.emit(this.articleCollection);
  }

  public getCustomArticleNumberLabel(article: ArticleVO): string {
    if (!article || !article.articleNo) {
      return '';
    }
    const mapping: ArticleNumberMappingVO = ArticleNumberMappingUtils.findMappingByArticleNumber(article.articleNo,
      this.userModel.articleNumberMappings);
    return !mapping ? '' : mapping.customArticleNumber;
  }

  public removeArticleFromArticleCollection(article: ArticleVO, articleCollection: ArticleCollectionVO): void {
    if (!articleCollection || ArrayUtils.isEmpty(this.collectionArticles)) {
      return;
    }
    this.removedArticles.push(...this.collectionArticles.splice(this.collectionArticles.indexOf(article), 1));
    this.update.emit();
  }

  public editArticleNumberMappingPopup(): void {
    this.articleComponentsPresenter.openEditArticleNumberMappingDialog(this.articleCollection);
  }

  public sortArticles(): void {
    if (this.articleCollection && ArrayUtils.isNotEmpty(this.collectionArticles)) {
      this.collectionArticles.sort((a, b) => this.currentSorting === ArticleCollectionVO.SORT_BY_ARTICLE_NO
        ? StringUtils.stringCompare(a.articleNo, b.articleNo)
        : StringUtils.stringCompare(this.getCustomArticleNumberLabel(a), this.getCustomArticleNumberLabel(b)));
    }
  }

  public accept(): void {
    if (!this.editForm.valid) {
      this.showRequiredFields = true;
      return;
    }
    const articleCollectionName: string = this.editForm.controls['articleCollectionName'].value;
    if (this.articleCollection) {
      this.articleCollection.sorting = this.currentSorting;
      this.applyRemovalArticlesFromCollection();
    } else {
      this.articleCollection = new ArticleCollectionVO();
    }
    this.articleCollection.name = articleCollectionName;
    this.close();
    this.collectionService.notifyCollectionUpdated();
  }

  public checkControlValidity(controlName: string): boolean {
    const control = this.editForm.controls[controlName];
    return (control.invalid && control.touched) || (control.invalid && this.showRequiredFields);
  }

  private getArticleCollectionLabel(articleCollectionName: string): string {
    return this.localeResourceManager.getResolvedString(articleCollectionName);
  }

  public getCustomArticleNumberMappingLabel(): string {
    let label: string = this.localeResourceManager.getValueByKey('editArticlePopup.own_article_number');
    const activeArticleNumberMappingListName: string = this.userModel.getActiveArticleNumberMappingListName();
    if (activeArticleNumberMappingListName) {
      label += ' (' + activeArticleNumberMappingListName + ')';
    }
    return label;
  }

  private applyRemovalArticlesFromCollection(): void {
    if (ArrayUtils.isNotEmpty(this.removedArticles)) {
      this.removedArticles.forEach(article => {
        ArticleCollectionUtils.removeArticleFromArticleCollection(this.articleCollection, article.identity);
      });
      for (let collection of this.userModel.userProfile.articleCollections) {
        if (collection.id === this.articleCollection.id) {
          collection = this.articleCollection;
          break;
        }
      }
    }
  }

  // TODO: this handler could be refactored into separate handlers, once changes of SMAR-475 are included/approved
  private articleNumberMappingsUpdatedHandler(): void {
    if (this.articleCollection) {
      const collection: ArticleCollectionVO = this.userModel.userProfile.articleCollections.find(c => c.id === this.articleCollection.id);
      if (collection && this.collectionArticles.length + this.removedArticles.length === collection.articleIds.length) {
        // no articles were added to this collection by using functionality to add articles as favorites from artilce number mappings dialog,
        // but sorting could have been effected for currently edited collection by changed custom article numbers:
        this.sortArticles();
      } else {
        // new articles were added (as favorites) from artilce number mappings dialog to the collection currently edited
        // we update edited collection to refelect such updated state. Any previously removed articles are included again:
        this.articleManager.getArticles(collection.articleIds).subscribe(articles => {
          this.removedArticles = [];
          this.collectionArticles = articles;
          this.sortArticles();
          this.update.emit();
        });
      }
    }
  }
}
